import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { XCircle, AlertCircle } from 'lucide-react';
export function ProblemSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section className="py-20 px-4 md:px-6 bg-white/50 relative overflow-hidden" data-id="element-190">
      <div className="max-w-4xl mx-auto" data-id="element-191">
        {/* Main Problem Statement */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="text-center mb-20" data-id="element-192">
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-sage-dark mb-6" data-id="element-193">
            The real problem isn’t your kid
          </motion.h2>
          <motion.p variants={itemVariants} className="text-lg md:text-xl text-sage-dark/80 leading-relaxed mb-8" data-id="element-194">
            If after school feels like walking into an emotional storm, you’re
            not alone. Millions of moms watch “after school meltdown” videos
            because it’s the one problem that makes you think:
            <span className="italic font-serif text-terracotta block mt-2 text-2xl" data-id="element-195">
              “I’m a gentle parent… so why am I yelling by dinner?”
            </span>
          </motion.p>
          <motion.div variants={itemVariants} className="bg-sage-light/20 p-8 rounded-2xl border border-sage-light/30" data-id="element-196">
            <p className="text-lg font-medium text-sage-dark" data-id="element-197">
              But here’s the shift: Your child isn’t giving you a hard time.{' '}
              <br data-id="element-198" />
              <span className="font-bold text-terracotta" data-id="element-199">
                They’re having a hard time.
              </span>{' '}
              <br data-id="element-200" />
              And the reason it shows up at 4PM is painfully logical once you
              see it.
            </p>
          </motion.div>
        </motion.div>

        {/* Why School Drains Kids */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-24" data-id="element-201">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-202">
            <h3 className="text-2xl md:text-3xl font-bold text-sage-dark mb-6" data-id="element-203">
              Why school drains kids{' '}
              <span className="text-sage-DEFAULT font-normal block text-xl mt-1" data-id="element-204">
                (even the “well-behaved” ones)
              </span>
            </h3>
            <p className="text-lg text-sage-dark/80 mb-6" data-id="element-205">
              Your child spends all day doing invisible hard things:
            </p>
            <ul className="space-y-3" data-id="element-206">
              {['following instructions', 'managing sensory overload', 'staying still when their body wants to move', 'sharing attention', 'transitioning on demand', 'holding it together socially'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-207">
                  <div className="mt-1.5 w-2 h-2 rounded-full bg-terracotta flex-shrink-0" data-id="element-208" />
                  <span className="text-sage-dark/90 font-medium" data-id="element-209">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-dusty-blue/20 p-8 rounded-3xl relative" data-id="element-210">
            <div className="absolute -top-4 -right-4 w-12 h-12 bg-dusty-blue rounded-full flex items-center justify-center text-white" data-id="element-211">
              <AlertCircle size={24} data-id="element-212" />
            </div>
            <p className="text-lg text-sage-dark/90 leading-relaxed italic" data-id="element-213">
              "So when they finally get to you—their safest person—the system
              releases. Not because you’re doing it wrong… but because you’re
              the place they can fall apart. That’s why typical advice fails.
              Because it treats 4PM like a normal moment. It’s not."
            </p>
          </motion.div>
        </div>

        {/* Failure of Old Methods */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerVariants} className="bg-white rounded-3xl p-8 md:p-12 shadow-xl shadow-sage-dark/5 border border-sage-light/20" data-id="element-214">
          <motion.h3 variants={itemVariants} className="text-2xl md:text-3xl font-bold text-sage-dark mb-8 text-center" data-id="element-215">
            The failure of old methods
          </motion.h3>

          <div className="grid md:grid-cols-2 gap-6 mb-10" data-id="element-216">
            {[{
            title: 'Snacks',
            desc: 'sometimes helps, sometimes makes it worse'
          }, {
            title: 'Screens',
            desc: 'calm now, explosive later'
          }, {
            title: 'Talking about feelings',
            desc: 'they can’t even hear you'
          }, {
            title: 'Consequences',
            desc: 'creates power struggles'
          }, {
            title: '“Calm down” scripts',
            desc: 'that feel like gasoline'
          }].map((item, i) => <motion.div key={i} variants={itemVariants} className="flex items-start gap-4 p-4 rounded-xl bg-red-50/50 border border-red-100" data-id="element-217">
                <XCircle className="w-6 h-6 text-terracotta flex-shrink-0 mt-0.5" data-id="element-218" />
                <div data-id="element-219">
                  <span className="font-bold text-sage-dark block" data-id="element-220">
                    {item.title}
                  </span>
                  <span className="text-sm text-sage-dark/70" data-id="element-221">{item.desc}</span>
                </div>
              </motion.div>)}
          </div>

          <motion.div variants={itemVariants} className="text-center border-t border-sage-light/20 pt-8" data-id="element-222">
            <p className="text-xl font-medium text-sage-dark" data-id="element-223">
              None of that addresses the real issue:{' '}
              <span className="text-terracotta font-bold" data-id="element-224">
                their regulation tank is empty.
              </span>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}